import { Alert } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { MutableRefObject } from "react";
import { useCallback, useMemo } from "react";
import { useOutletContext } from "react-router";

import ErrorPage from "@/components/ErrorPage";
import { useGetHotelDepartureCustomPassengers, useGetHotelDeparturePlanning } from "@/requests/hotel-departure.queries";
import { IHotelPlanningDepartureResponseItem } from "@/schemas/hotelPlanningDepartureSchema";
import {
    HotelPlanningDepartureFilters,
    useHotelPlanningDepartureDate,
    useHotelPlanningDepartureFilters,
    useHotelPlanningDepartureIsPastDate,
} from "@/stores/use-hotel-planning-departure-table";

import HotelPlanningDepartureTable from "./components/table";
import HotelDepartureProgressDialog from "./components/table/HotelDepartureProgressDialog";

const filterHotelDepartureTransfers = (
    transfers: IHotelPlanningDepartureResponseItem[],
    { status, selectedDrivers }: HotelPlanningDepartureFilters
) => {
    if (!status && !selectedDrivers.length) return transfers;
    return transfers.filter(
        ({ status: transferStatus, assignedUser }) =>
            (!status || transferStatus === status) &&
            (!selectedDrivers.length ||
                (assignedUser && assignedUser.userId && selectedDrivers.includes(assignedUser.userId)))
    );
};

function HotelPlanningDeparturePage() {
    const { api } = useOutletContext<{ api: MutableRefObject<GridApiPremium> }>();
    const date = useHotelPlanningDepartureDate();
    const isPastDate = useHotelPlanningDepartureIsPastDate();
    const clinic = useGetHotelDeparturePlanning(date);
    const custom = useGetHotelDepartureCustomPassengers(date);
    const allRows = useMemo<IHotelPlanningDepartureResponseItem[]>(
        () => [...(clinic.data || []), ...(custom.data || [])],
        [clinic.data, custom.data]
    );
    const filters = useHotelPlanningDepartureFilters();
    const rows = useMemo(() => filterHotelDepartureTransfers(allRows, filters), [allRows, filters]);

    const loading = clinic.isLoading || clinic.isFetching || custom.isLoading || custom.isFetching;
    const refetch = useCallback(() => {
        clinic.refetch();
        custom.refetch();
    }, [clinic, custom]);

    // Clinic is the primary/blocking source. Custom failures degrade to an empty list (handled in the hook).
    if (clinic.isError) {
        return <ErrorPage />;
    }

    return (
        <>
            {isPastDate && (
                <Alert severity="info" className="!py-0 !mt-1">
                    Geçmiş görünüm: yalnızca gerçekleşen klinik transferleri listelenir, değişiklik yapılamaz.
                </Alert>
            )}
            <HotelPlanningDepartureTable
                refetch={refetch}
                rows={rows}
                loading={loading}
                apiRef={api}
                allRows={allRows}
            />
            {!isPastDate && <HotelDepartureProgressDialog />}
        </>
    );
}

export default HotelPlanningDeparturePage;
